import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle,
  faChevronDown,
  faChevronRight,
  faUsers,
  faTicket
} from '@fortawesome/free-solid-svg-icons';
import './TicketTable.css';

const TicketTable = ({ tickets, onLoadMore, hasMore, loading }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Format amount
  const formatAmount = (amountCents) => {
    return `$${(amountCents / 100).toFixed(2)}`;
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Toggle row expansion
  const toggleRow = (ticketId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(ticketId)) {
      newExpanded.delete(ticketId);
    } else {
      newExpanded.add(ticketId);
    }
    setExpandedRows(newExpanded);
  };

  // Parse and render attendee names
  const renderAttendeeNames = (ticket) => {
    if (!ticket.attendee_names) {
      return (
        <div className="attendee-summary">
          <FontAwesomeIcon icon={faUsers} className="attendee-icon" />
          <span className="attendee-count">{ticket.attendee_count || 1} {ticket.attendee_count === 1 ? 'attendee' : 'attendees'}</span>
        </div>
      );
    }

    try {
      // Parse attendee_names JSONB
      const attendees = typeof ticket.attendee_names === 'string' 
        ? JSON.parse(ticket.attendee_names) 
        : ticket.attendee_names;

      if (!Array.isArray(attendees) || attendees.length === 0) {
        return (
          <div className="attendee-summary">
            <FontAwesomeIcon icon={faUsers} className="attendee-icon" />
            <span className="attendee-count">{ticket.attendee_count || 1} {ticket.attendee_count === 1 ? 'attendee' : 'attendees'}</span>
          </div>
        );
      }

      const isExpanded = expandedRows.has(ticket.id);

      return (
        <div className="attendee-container">
          <button 
            className="attendee-summary clickable"
            onClick={(e) => {
              e.stopPropagation();
              toggleRow(ticket.id);
            }}
          >
            <FontAwesomeIcon 
              icon={isExpanded ? faChevronDown : faChevronRight} 
              className="expand-icon" 
            />
            <FontAwesomeIcon icon={faUsers} className="attendee-icon" />
            <span className="attendee-count">
              {attendees.length} {attendees.length === 1 ? 'attendee' : 'attendees'}
            </span>
          </button>

          {isExpanded && (
            <div className="attendee-details">
              {attendees.map((attendee, index) => {
                // Handle different attendee formats
                let attendeeName = '';
                let ticketType = null;
                let ticketLabel = null;
                let pricePaid = null;

                if (typeof attendee === 'string') {
                  // Simple string format
                  attendeeName = attendee;
                } else if (typeof attendee === 'object' && attendee !== null) {
                  // Object format with more details
                  const firstName = attendee.firstName || attendee.first_name || '';
                  const lastName = attendee.lastName || attendee.last_name || '';
                  attendeeName = attendee.name || attendee.full_name || `${firstName} ${lastName}`.trim() || '';
                  
                  ticketType = attendee.ticket_type || attendee.ticketType;
                  ticketLabel = attendee.ticket_label || attendee.ticketLabel;
                  pricePaid = attendee.price_paid_cents ?? attendee.pricePaidCents;
                }

                return (
                  <div key={index} className="attendee-item">
                    <div className="attendee-info">
                      <FontAwesomeIcon icon={faTicket} className="ticket-icon" />
                      <div className="attendee-details-text">
                        <div className="attendee-name">{attendeeName || `Attendee ${index + 1}`}</div>
                        {ticketLabel && (
                          <div className="ticket-type-label">{ticketLabel}</div>
                        )}
                      </div>
                    </div>
                    {pricePaid !== null && pricePaid !== undefined && (
                      <div className="attendee-price">
                        {formatAmount(pricePaid)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    } catch (error) {
      console.error('Error parsing attendee names:', error);
      return (
        <div className="attendee-summary">
          <FontAwesomeIcon icon={faUsers} className="attendee-icon" />
          <span className="attendee-count">{ticket.attendee_count || 1} {ticket.attendee_count === 1 ? 'attendee' : 'attendees'}</span>
        </div>
      );
    }
  };

  // Render purchaser info
  const renderPurchaserInfo = (ticket) => {
    const primaryName = `${ticket.primary_attendee_first_name || ''} ${ticket.primary_attendee_last_name || ''}`.trim();
    
    if (!primaryName) {
      return (
        <div className="purchaser-info">
          <div className="purchaser-name">Anonymous</div>
        </div>
      );
    }

    return (
      <div className="purchaser-info">
        <div className="purchaser-name">{primaryName}</div>
      </div>
    );
  };

  // Render event info
  const renderEventInfo = (ticket) => {
    const event = ticket.events;
    
    if (!event) {
      return <span className="text-muted">Unknown Event</span>;
    }

    return (
      <div className="event-info">
        <div className="event-title">{event.title}</div>
        {event.date && (
          <div className="event-date text-muted">
            {formatDate(event.date)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="donation-table-container">
      <div className="table-wrapper">
        <table className="donation-table ticket-table">
          <thead>
            <tr>
              <th className="col-event">Event</th>
              <th className="col-purchaser">Purchaser</th>
              <th className="col-attendees">Attendees</th>
              <th className="col-amount">Total Amount</th>
              <th className="col-date">Purchase Date</th>
              <th className="col-confirmation">Confirmation #</th>
            </tr>
          </thead>
          <tbody>
            {!tickets || tickets.length === 0 ? (
              <tr className="empty-state-row">
                <td colSpan="6" className="empty-state-cell">
                  No ticket purchases yet.
                </td>
              </tr>
            ) : (
              tickets.map((ticket) => (
                <React.Fragment key={ticket.id}>
                  <tr className="donation-row ticket-row">
                    <td className="col-event">
                      {renderEventInfo(ticket)}
                    </td>
                    
                    <td className="col-purchaser">
                      {renderPurchaserInfo(ticket)}
                    </td>
                    
                    <td className="col-attendees">
                      {renderAttendeeNames(ticket)}
                    </td>
                    
                    <td className="col-amount">
                      <div className="amount-cell">
                        <div className="amount-primary">
                          {formatAmount(ticket.total_amount_cents)}
                        </div>
                        <div className="amount-secondary text-muted">
                          {formatAmount(ticket.price_per_ticket_cents)} per ticket
                        </div>
                      </div>
                    </td>
                    
                    <td className="col-date">
                      <div className="date-cell">
                        <div className="date-primary">
                          {formatDate(ticket.payment_completed_at || ticket.created_at)}
                        </div>
                        <div className="date-secondary text-muted">
                          {formatTime(ticket.payment_completed_at || ticket.created_at)}
                        </div>
                      </div>
                    </td>
                    
                    <td className="col-confirmation">
                      <div className="confirmation-cell">
                        {ticket.confirmation_number || '—'}
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Load more button */}
      {hasMore && (
        <div className="table-footer">
          <button 
            className="load-more-button"
            onClick={onLoadMore}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
    </div>
  );
};

export default TicketTable;
